import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  TrendingUp, 
  Star,
  CheckCircle,
  AlertTriangle,
  Clock,
  Target,
  Award,
  Users,
  DollarSign,
  FileText,
  BarChart3,
  TrendingDown,
  MoreHorizontal,
  RefreshCw,
  HelpCircle
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import ConfirmationModal from '../common/ConfirmationModal';
import PerformanceReviewService from '../../services/performanceReviewService';

// Custom Dropdown Component - exactly like Task Management
const ActionDropdown = ({ performance, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 200);
    }
    setIsOpen((v) => !v);
  };

  const handleView = () => {
    setIsOpen(false);
    performance && (performance.id || performance._id) && onView(performance);
  };

  const handleEdit = () => {
    setIsOpen(false);
    performance && (performance.id || performance._id) && onEdit(performance);
  };

  const handleDelete = () => {
    setIsOpen(false);
    performance && (performance.id || performance._id) && onDelete(performance);
  };

  const rect = buttonRef.current?.getBoundingClientRect();

  return (
    <div
      className="relative"
      ref={dropdownRef}
      style={{ zIndex: isOpen ? 9999 : 'auto' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
        style={{ position: 'relative', zIndex: isOpen ? 10000 : 'auto' }}
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && rect && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            top: openUpward
              ? window.innerHeight - rect.top + 8
              : rect.bottom + 8,
            left: rect.left,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
                onClick={handleView}
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View Details
              </button>
            </li>

            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
                onClick={handleEdit}
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit
              </button>
            </li>

            <li className="border-t border-gray-200">
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
                onClick={handleDelete}
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
 };

const PerformanceTracking = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [performanceData, setPerformanceData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [performanceToDelete, setPerformanceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 10,
    totalItems: 0,
    current: 1,
    pages: 1,
    limit: 10
  });

  // Transform backend performance review data to frontend format
  const transformPerformanceReview = (review) => {
    // Get subcontractor name
    const subcontractorName = review.subcontractor?.name || 'N/A';
    const contactPerson = review.subcontractor?.primaryContact?.name || 
                         (review.subcontractor?.primaryContact ? 
                          `${review.subcontractor.primaryContact.firstName || ''} ${review.subcontractor.primaryContact.lastName || ''}`.trim() : 
                          'N/A');

    // Calculate performance status based on overall score
    let performanceStatus = 'Average';
    if (review.overallScore >= 8) {
      performanceStatus = 'Excellent';
    } else if (review.overallScore >= 6) {
      performanceStatus = 'Good';
    } else if (review.overallScore >= 4) {
      performanceStatus = 'Average';
    } else {
      performanceStatus = 'Poor';
    }

    // Determine trend (simplified - can be enhanced based on historical data)
    let trend = 'stable';
    if (review.overallScore >= 8) {
      trend = 'up';
    } else if (review.overallScore < 4) {
      trend = 'down';
    }

    // Get safety incidents from metrics
    const safetyIncidents = review.safetyMetrics?.safetyIncidents || 0;

    // Calculate quality score percentage (assuming 0-10 scale, convert to percentage)
    const qualityScore = review.qualityScore ? Math.round((review.qualityScore / 10) * 100) : 0;
    const timelinessScore = review.timelinessScore ? Math.round((review.timelinessScore / 10) * 100) : 0;
    const safetyScore = review.safetyScore ? Math.round((review.safetyScore / 10) * 100) : 0;

    // Calculate overall rating (0-5 scale from 0-10 scale)
    const overallRating = review.overallScore ? (review.overallScore / 2).toFixed(1) : 0;

    return {
      id: review._id,
      _id: review._id,
      subcontractor: subcontractorName,
      contactPerson: contactPerson,
      category: review.subcontractor?.category || 'N/A',
      overallRating: parseFloat(overallRating),
      qualityScore: qualityScore,
      timelinessScore: timelinessScore,
      communicationScore: review.communicationScore ? Math.round((review.communicationScore / 10) * 100) : 0,
      safetyScore: safetyScore,
      costEffectivenessScore: 0, // Not in backend schema
      projectsCompleted: 0, // Would need aggregation
      onTimeDelivery: review.timelinessMetrics?.onTimeDelivery || 0,
      budgetAdherence: 0, // Not in backend schema
      clientSatisfaction: review.qualityMetrics?.customerSatisfaction || 0,
      safetyIncidents: safetyIncidents,
      lastProject: review.endDate ? new Date(review.endDate).toISOString().split('T')[0] : '',
      performance: performanceStatus,
      trend: trend,
      reviewData: review // Keep full data for reference
    };
  };

  useEffect(() => {
    loadPerformanceData();
  }, [pagination.page, searchTerm, ratingFilter, categoryFilter]);

  // Debounce search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (pagination.page === 1) {
        loadPerformanceData();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const loadPerformanceData = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page || 1,
        limit: pagination.itemsPerPage || 10,
        ...(searchTerm && { search: searchTerm }),
        ...(ratingFilter && { rating: ratingFilter }),
        ...(categoryFilter && { category: categoryFilter })
      };

      const response = await PerformanceReviewService.getPerformanceReviews(params);
      
      if (response && response.success) {
        const transformedReviews = (response.data || []).map(transformPerformanceReview);
        setPerformanceData(transformedReviews);
        
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            current: response.pagination.current || response.pagination.page || 1,
            pages: response.pagination.pages || 1,
            totalItems: response.pagination.total || 0,
            limit: response.pagination.limit || 10
          }));
        }
      } else {
        setPerformanceData([]);
        if (response && !response.data) {
          showError('Failed to load performance reviews.');
        }
      }
    } catch (err) {
      console.error('Error loading performance data:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to load performance reviews.';
      showError(errorMessage);
      setPerformanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadPerformanceData();
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/performance-tracking';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh performance data',
      disabled: loading
    },
    {
      label: 'Add Performance Review',
      icon: <Plus size={18} />,
      onClick: () => navigate('/subcontracting/performance/create'),
      variant: 'primary',
      tooltip: 'Add a new performance review'
    }
  ];

  const handleView = (performance) => {
    const performanceId = performance._id || performance.id;
    if (performanceId) {
      navigate(`/subcontracting/performance/view/${performanceId}`);
    } else {
      console.error('Performance review ID not found:', performance);
      showError('Unable to view performance review: ID not found');
    }
  };

  const handleEdit = (performance) => {
    const performanceId = performance._id || performance.id;
    if (performanceId) {
      navigate(`/subcontracting/performance/edit/${performanceId}`);
    } else {
      console.error('Performance review ID not found:', performance);
      showError('Unable to edit performance review: ID not found');
    }
  };

  const handleDelete = (performance) => {
    setPerformanceToDelete(performance);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!performanceToDelete) return;
    
    setIsDeleting(true);
    try {
      const performanceId = performanceToDelete._id || performanceToDelete.id;
      if (!performanceId) {
        throw new Error('Performance review ID not found');
      }
      
      await PerformanceReviewService.deletePerformanceReview(performanceId);
      success('Performance review deleted successfully!');
      await loadPerformanceData();
      setShowDeleteModal(false);
      setPerformanceToDelete(null);
    } catch (err) {
      console.error('Error deleting performance review:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to delete performance review.';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const getPerformanceBadge = (performance) => {
  const configMap = {
    Excellent: { color: '#10b981', bg: '#d1fae5' },
    Good:      { color: '#3b82f6', bg: '#dbeafe' },
    Average:   { color: '#f59e0b', bg: '#fef3c7' },
    Poor:      { color: '#ef4444', bg: '#fee2e2' }
    };
    
  const config = configMap[performance] || {
    color: '#6b7280',
    bg: '#f3f4f6'
  };

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.color}20`,
        whiteSpace: 'nowrap'
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: config.color }}
      />
      {performance}
    </span>
  );
};
  const ScoreProgress = ({ value, color }) => (
  <div style={{ width: '90px' }}>
    <div className="d-flex justify-content-between mb-1">
      <span style={{ fontSize: '12px', fontWeight: 600 }}>{value}%</span>
    </div>
    <div
      style={{
        height: '6px',
        backgroundColor: '#e5e7eb',
        borderRadius: '4px',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          width: `${value}%`,
          height: '100%',
          backgroundColor: color,
          borderRadius: '4px',
          transition: 'width 0.3s ease'
        }}
      />
    </div>
  </div>
);

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp size={16} className="text-success" />;
      case 'down':
        return <TrendingDown size={16} className="text-danger" />;
      default:
        return <BarChart3 size={16} className="text-info" />;
    }
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'subcontractor',
      label: 'Subcontractor',
      render: (value, performance) => (
        <div>
          <div className="fw-bold text-truncate" style={{ maxWidth: '150px' }} title={performance.subcontractor}>
            {performance.subcontractor}
          </div>
          <small className="text-muted">{performance.contactPerson}</small>
        </div>
      )
    },
    {
      key: 'overallRating',
      label: 'Overall Rating',
      render: (value, performance) => (
        <div className="d-flex align-items-center">
          <Star size={16} className="text-warning me-1" />
          <span className="fw-bold">{performance.overallRating}</span>
          <div className="ms-2">
            {getTrendIcon(performance.trend)}
          </div>
        </div>
      )
    },
    {
      key: 'qualityScore',
      label: 'Quality Score',
      render: (_, p) => <ScoreProgress value={p.qualityScore} color="#10b981" />
    },
    {
      key: 'timelinessScore',
      label: 'Timeliness',
      render: (_, p) => <ScoreProgress value={p.timelinessScore} color="#3b82f6" />
    },
    {
      key: 'safetyScore',
      label: 'Safety Score',
      render: (_, p) => <ScoreProgress value={p.safetyScore} color="#f59e0b" />
    },
    {
      key: 'performance',
      label: 'Performance',
      render: (value, performance) => getPerformanceBadge(performance.performance)
    },
    {
      key: 'projectsCompleted',
      label: 'Projects',
      render: (value, performance) => (
        <div>
          <div className="fw-bold text-truncate d-block" style={{ maxWidth: '100px' }} title={performance.projectsCompleted}>
            {performance.projectsCompleted}
          </div>
          <small className="text-muted text-truncate d-block" style={{ maxWidth: '100px' }} title="Completed">
            Completed
          </small>
        </div>
      )
    },
    {
      key: 'safetyIncidents',
      label: 'Safety',
      render: (value, performance) => (
        <div>
          <div className="fw-bold text-truncate d-block" style={{ maxWidth: '100px' }} title={performance.safetyIncidents}>
            {performance.safetyIncidents}
          </div>
          <small className="text-muted text-truncate d-block" style={{ maxWidth: '100px' }} title="Incidents">
            Incidents
          </small>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, performance) => {
        if (!performance || !performance.id) {
          console.error('Invalid performance data in render function:', performance);
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <ActionDropdown 
            performance={performance}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        );
      }
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Performance Tracking"
        description="Monitor subcontractor performance and quality metrics"
        actions={headerActions}
      />
      <div className="page-container">
      {/* Performance Tracking Table */}
      <DataTable
        data={performanceData}
        columns={columns}
        title="Performance Tracking"
        loading={loading}
        pagination={pagination}
        onPaginationChange={setPagination}
        emptyMessage="No performance records found. Click 'Add Performance Review' to get started."
        showActions={false}
      />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setPerformanceToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Performance Review"
        message={`Are you sure you want to delete the performance review for ${performanceToDelete?.subcontractor || 'this subcontractor'}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default PerformanceTracking;
